import React, { useEffect, useState } from "react";
import { Box, Typography, Dialog, Button, Avatar } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSystems } from "../../../../context/SystemContext";
import { SYSTEMS_REGISTRY } from "./systemsRegistry";
import DashboardLayout from "../DashboardLayout";
import BolsaSystemWrapper from "./BolsaSystemWrapper";
import ScrappingSystemWrapper from "./ScrappingSystemWrapper";
import TrellohitSystemWrapper from "./TrellohitSystemWrapper";
import ExternalSystemHandshake from "./ExternalSystemHandshake";
import InternalSystemWrapper from "./InternalSystemWrapper";
import CommunityWall from "../community/CommunityWall";
import GlobalMessenger from "../mensajes/GlobalMessenger";
import axios from "axios";
import ProPlanModal from "../../../components/pro/ProPlanModal";
import { useAuth } from "../../../../context/AuthContext";

const SystemIframeContainer = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { activatedSystems, setSystemLoaded, activeProfile, setActiveProfile } = useSystems();

  // ============================================
  // Todos los sistemas iframe deben usar el mismo
  // patrón de rutas (routes.jsx + basename dinámico)
  // para que la navegación funcione correctamente.
  // ============================================
  const isSystemsPath = location.pathname.startsWith("/systems/");
  const pathParts = location.pathname.split("/");
  const activeSystemId = isSystemsPath && pathParts.length >= 3 ? pathParts[2] : null;

  const hasLoadedOnce = React.useRef(false);

  React.useEffect(() => {
    if (!loading) {
      hasLoadedOnce.current = true;
    }
  }, [loading]);

  useEffect(() => {
    const userId = user?.id || user?.user_id;
    if (!activeSystemId || !userId) return;
    const logEvent = async (type) => {
      try {
        await axios.post('https://apiweb.hitpoly.com/ajax/systemTrackingController.php', {
          user_id: userId,
          system_id: activeSystemId,
          event_type: type
        });
      } catch (err) {}
    };
    logEvent('enter');
    return () => { logEvent('exit'); };
  }, [activeSystemId, user]);

  const activeSystem = activeSystemId
    ? SYSTEMS_REGISTRY.find(s => s.id === activeSystemId)
    : null;

  const [isProModalOpen, setIsProModalOpen] = useState(false);
  const [trialVencido, setTrialVencido] = useState(false);
  const [isCreateProfileDialogOpen, setIsCreateProfileDialogOpen] = useState(false);
  const [profileChooserOpen, setProfileChooserOpen] = useState(false);
  const [chooserProfiles, setChooserProfiles] = useState([]);

  // Popup: elegir con qué perfil entrar (profesional / empresa) y re-verificar trial
  const seleccionarPerfilDeEntrada = (p) => {
    setProfileChooserOpen(false);
    setActiveProfile({ id: p.id, type: p.type, name: p.name, label: p.label, avatar: p.avatar });
    // Re-verificar trial con el perfil ya elegido
    try {
      const raw = localStorage.getItem("hitpoly_trial");
      if (raw) {
        const t = JSON.parse(raw);
        if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
          setIsProModalOpen(false);
          setTrialVencido(false);
          return;
        }
      }
    } catch (e) {}
    const uid = user?.id || user?.user_id;
    fetch("https://apiweb.hitpoly.com/ajax/trialsController.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ accion: "estado", usuario_id: uid || 1500 }),
    })
      .then((r) => r.json())
      .then((d) => {
        const finFuture = d && d.fin && new Date(d.fin).getTime() > Date.now();
        const activoReal = d && d.status === "success" && (d.activo || finFuture);
        setIsProModalOpen(!activoReal);
        setTrialVencido(!activoReal && !!(d && d.fin));
      })
      .catch(() => setIsProModalOpen(true));
  };

  // Guarda el destino pendiente + tipo de creación y entra a la bolsa.
  const iniciarCreacionPerfil = (tipo) => {
    try {
      let profiles = [];
      try {
        profiles = JSON.parse(localStorage.getItem("cached_profiles") || "[]") || [];
      } catch (e) {}
      const before = profiles.map((p) => ({ id: p.id, type: p.type }));
      localStorage.setItem("pending_pro_destination", JSON.stringify({ route: location.pathname + location.search, before, t: Date.now() }));
    } catch (e) {}
    sessionStorage.setItem("bolsa_crear_tipo", tipo);
    setIsCreateProfileDialogOpen(false);
    navigate("/systems/bolsa?reset=true");
  };

  const activationData = activeSystemId ? activatedSystems[activeSystemId] : null;

  // Verificar acceso Pro al cargar el sistema
  useEffect(() => {
    // Si el usuario acaba de elegir ir a un servicio desde el popup (CTA),
    // NO reabrir el popup automaticamente en esta navegacion al proyecto.
    const rawNav = sessionStorage.getItem("hitpoly_popup_nav");
    if (rawNav) {
      sessionStorage.removeItem("hitpoly_popup_nav");
      try {
        const nav = JSON.parse(rawNav);
        const fresco = nav && nav.sys && (Date.now() - nav.t) < 60000;
        if (fresco && activeSystem && nav.sys === activeSystem.id) return;
      } catch (e) {}
    }
    if (!activeSystem || !activeSystem.isPro) {
      setIsProModalOpen(false);
      return;
    }
    // Gate de tipo de perfil: los sistemas Pro (crm / bolsa) requieren un perfil
    // profesional o empresa. El perfil principal no entra directo.
    const tipoActivo = activeProfile?.type;
    if (tipoActivo !== "professional" && tipoActivo !== "enterprise") {
      let profiles = [];
      try {
        profiles = JSON.parse(localStorage.getItem("cached_profiles") || "[]") || [];
      } catch (e) {}
      const candidates = profiles.filter((p) => p.type === "professional" || p.type === "enterprise");
      if (candidates.length >= 2) {
        // Tiene profesional Y empresa: preguntar con cuál entrar
        setChooserProfiles(candidates);
        setProfileChooserOpen(true);
        return;
      }
      if (candidates.length === 1) {
        // Solo un perfil pro/empresa: activarlo y continuar con el gate de trial
        const only = candidates[0];
        setActiveProfile({ id: only.id, type: only.type, name: only.name, label: only.label, avatar: only.avatar });
      } else if (activeSystem.id === "bolsa") {
        // La bolsa es el camino de creación de perfil: permitir entrada
        setIsProModalOpen(false);
        return;
      } else {
        // Sin perfil pro/empresa: pedir crear uno y reanudar el destino después
        setIsCreateProfileDialogOpen(true);
        return;
      }
    }
    // NO insertar datos falsos por defecto; confiar solo en API o localStorage real
    try {
      const raw = localStorage.getItem("hitpoly_trial");
      if (raw) {
        const t = JSON.parse(raw);
        if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
          setIsProModalOpen(false);
          setTrialVencido(false);
          return;
        } else {
          localStorage.removeItem("hitpoly_trial");
          // No establecer vencido automáticamente; consultar API
        }
      }
    } catch (e) {
    }
    // Consultar API para verificar estado real
    const uid = user?.id || user?.user_id;
    const apiUrl = "https://apiweb.hitpoly.com/ajax/trialsController.php";
    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ accion: "estado", usuario_id: uid || 1500 }),
    })
      .then((r) => r.json())
      .then((d) => {
        const finFuture = (d && d.fin && new Date(d.fin).getTime() > Date.now());
        const activoReal = (d && d.status === "success" && (d.activo || finFuture));
        if (activoReal) {
          setIsProModalOpen(false);
          setTrialVencido(false);
        } else if (d && d.status === "success" && !d.activo) {
          try {
            const raw = localStorage.getItem("hitpoly_trial");
            if (raw) {
              const t = JSON.parse(raw);
              if (t && t.fin && new Date(t.fin).getTime() <= Date.now()) {
                localStorage.removeItem("hitpoly_trial");
                setTrialVencido(true);
              } else {
                localStorage.removeItem("hitpoly_trial");
                setTrialVencido(false);
              }
            } else {
              setTrialVencido(!!(d && d.fin));
            }
          } catch (e) {
            setTrialVencido(false);
          }
          setIsProModalOpen(true);
        } else {
          // Si hay error en API, confiar en localStorage si está vigente; de lo contrario, abrir popup
          try {
            const raw = localStorage.getItem("hitpoly_trial");
            if (raw) {
              const t = JSON.parse(raw);
              if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
                setIsProModalOpen(false);
                setTrialVencido(false);
              } else {
                localStorage.removeItem("hitpoly_trial");
                setTrialVencido(!!raw);
                setIsProModalOpen(true);
              }
            } else {
              setTrialVencido(false);
              setIsProModalOpen(true);
            }
          } catch (e) {
            setTrialVencido(false);
            setIsProModalOpen(true);
          }
        }
      })
      .catch((err) => {
        try {
          const raw = localStorage.getItem("hitpoly_trial");
          if (raw) {
            const t = JSON.parse(raw);
            if (t && t.fin && new Date(t.fin).getTime() > Date.now()) {
              setIsProModalOpen(false);
              setTrialVencido(false);
            } else {
              localStorage.removeItem("hitpoly_trial");
              setTrialVencido(!!raw);
              setIsProModalOpen(true);
            }
          } else {
            setTrialVencido(false);
            setIsProModalOpen(true);
          }
        } catch (e) {
          setTrialVencido(false);
          setIsProModalOpen(true);
        }
      });
  }, [activeSystem, user]);

  const renderActiveSystem = () => {
    if (!activeSystem) return null;

    if (activeSystem.isPro && isProModalOpen) {
      return null;
    }

    if (activeSystem.isPro && isCreateProfileDialogOpen) {
      return null;
    }

    if (activeSystem.isPro && profileChooserOpen) {
      return null;
    }

    if (activeSystem.isInternal) {
      if (activeSystem.id === 'comunidad') {
        return (
          <InternalSystemWrapper system={activeSystem}>
            <Box sx={{ height: "100%", overflowY: "auto" }}>
              <CommunityWall isPersistent={true} />
            </Box>
          </InternalSystemWrapper>
        );
      }
      if (activeSystem.id === 'mensajes') {
        return (
          <InternalSystemWrapper system={activeSystem}>
            <Box sx={{ height: "100%", overflowY: "hidden" }}>
              <GlobalMessenger />
            </Box>
          </InternalSystemWrapper>
        );
      }
      return null;
    }

    if (!activationData?.activated) return null;

    const system = activeSystem;

    if (system.id === 'bolsa') {
      return (
        <BolsaSystemWrapper
          key={`${system.id}-${activeProfile?.id || ''}-${activeProfile?.type || ''}-${activationData.timestamp}`}
          system={system}
          activationData={activationData}
          onLoaded={() => setSystemLoaded(system.id)}
        />
      );
    }
    if (system.id === 'scrapping') {
      return (
        <ScrappingSystemWrapper
          key={`${system.id}-${activeProfile?.id || ''}-${activeProfile?.type || ''}-${activationData.timestamp}`}
          system={system}
          activationData={activationData}
          onLoaded={() => setSystemLoaded(system.id)}
        />
      );
    }
    if (system.id === 'proyectos') {
      return (
        <TrellohitSystemWrapper
          key={`${system.id}-${activeProfile?.id || ''}-${activeProfile?.type || ''}-${activationData.timestamp}`}
          system={system}
          activationData={activationData}
          onLoaded={() => setSystemLoaded(system.id)}
        />
      );
    }
    return (
      <ExternalSystemHandshake
        key={`${system.id}-${activationData.timestamp}-${activeProfile?.id || ''}-${activeProfile?.type || ''}`}
        system={system}
        activationData={activationData}
        onLoaded={() => setSystemLoaded(system.id)}
      />
    );
  };
 
  if (loading && !hasLoadedOnce.current) {
    return (
      <Box 
        sx={{ 
          position: "fixed", 
          top: 0, left: 0, right: 0, bottom: 0, 
          zIndex: isSystemsPath ? 9991 : -1,
          display: isSystemsPath ? "flex" : "none",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#0f0f1a",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(108,77,226,0.25) 0%, transparent 70%)", filter: "blur(40px)", animation: "sif-pulse 2.4s ease-in-out infinite", "@keyframes sif-pulse": { "0%, 100%": { transform: "scale(1)", opacity: 0.7 }, "50%": { transform: "scale(1.25)", opacity: 1 } } }} />
        <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", border: "2px solid transparent", borderTopColor: "#6C4DE2", borderRightColor: "rgba(108,77,226,0.3)", animation: "sif-spin 1s linear infinite", "@keyframes sif-spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
        <Box sx={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box component="img" src="/images/isotipo-hitpoly.png" alt="" sx={{ width: 72, height: 72, borderRadius: "16px", boxShadow: "0 8px 40px rgba(108,77,226,0.5)", animation: "sif-float 2s ease-in-out infinite", "@keyframes sif-float": { "0%, 100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(-8px)" } } }} />
          <Typography variant="body2" sx={{ mt: 3, color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: 1 }}>HitPoly</Typography>
          <Typography variant="caption" sx={{ mt: 1, color: "#6a7085", letterSpacing: 2, textTransform: "uppercase" }}>Sincronizando sesión segura...</Typography>
        </Box>
      </Box>
    );
  }

  if (!isSystemsPath) return null;

  return (
    <>
      <DashboardLayout disablePadding={true}>
        {renderActiveSystem()}
      </DashboardLayout>
      <ProPlanModal
        open={isProModalOpen}
        vencido={trialVencido}
        onClose={() => setIsProModalOpen(false)}
        herramienta={activeSystem?.title || ""}
        onTrialStarted={(fin) => {
          localStorage.setItem("hitpoly_trial", JSON.stringify({ fin, origen: "prueba" }));
          window.location.reload();
        }}
      />
      {/* Popup: elegir con qué perfil entrar (profesional / empresa) */}
      <Dialog
        open={profileChooserOpen}
        onClose={() => setProfileChooserOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: "90%" } }}
        sx={{ zIndex: 30000 }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            ¿Con qué perfil quieres entrar?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {activeSystem?.title
              ? `Tienes más de un perfil disponible. Selecciona con cuál quieres acceder a ${activeSystem.title}.`
              : "Tienes más de un perfil disponible. Selecciona con cuál quieres continuar."}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {chooserProfiles.map((p) => (
              <Button
                key={`${p.type}-${p.id}`}
                variant="outlined"
                fullWidth
                onClick={() => seleccionarPerfilDeEntrada(p)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: "flex-start",
                  textTransform: "none",
                  gap: 1.5,
                }}
                startIcon={
                  p.avatar ? (
                    <Avatar src={p.avatar} sx={{ width: 28, height: 28 }} />
                  ) : (
                    <Avatar sx={{ width: 28, height: 28, bgcolor: "#6C4DE2" }}>
                      {p.type === "enterprise" ? "E" : "P"}
                    </Avatar>
                  )
                }
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography fontWeight={700} color="text.primary">
                    {p.name || (p.type === "enterprise" ? "Perfil Empresa" : "Perfil Profesional")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.label || (p.type === "enterprise" ? "Empresa" : "Profesional")}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </Box>
      </Dialog>
      {/* Gate de creación de perfil al entrar directo a un sistema Pro */}
      <Dialog
        open={isCreateProfileDialogOpen}
        onClose={() => setIsCreateProfileDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: "90%" } }}
        sx={{ zIndex: 30000 }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Crea un perfil para continuar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {activeSystem?.title
              ? `Para acceder a ${activeSystem.title} necesitas un perfil profesional o de empresa.`
              : "Necesitas un perfil profesional o de empresa para acceder a esta sección."}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => iniciarCreacionPerfil("professional")}
              sx={{ borderRadius: 2, py: 1.5, bgcolor: "#F21C63", "&:hover": { bgcolor: "#6C4DE2" } }}
            >
              Crear perfil profesional
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => iniciarCreacionPerfil("enterprise")}
              sx={{ borderRadius: 2, py: 1.5 }}
            >
              Crear perfil empresa
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default SystemIframeContainer;
